/**
 * PreviewVoluumSection — live preview summary + full Standard paths workspace.
 *
 * Reads the in-flight Voluum flow draft from the shared StandardDraftContext
 * so any edit here (toggling a path, nudging a weight) is reflected by the
 * live preview API call without leaving the tab. The draft is also rendered
 * by the Standard tab editor — both consume the same source of truth.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';
import { AlertCircle } from 'lucide-react';
import type { FlowOptimizerPreviewResult } from '@trafficpulse/shared';

import { LoadingSpinner } from '@/components/ui/feedback/LoadingSpinner';

import { PreviewIndexProvider } from '../components/preview/PreviewIndexContext';
import PreviewSummary from '../components/preview/PreviewSummary';
import { StandardPathsWorkspace } from './standard/v2/sections';
import { useStandardDraftContext } from '../standard-draft';

export interface PreviewVoluumSectionProps {
  result: FlowOptimizerPreviewResult;
  applied: boolean;
}

const PreviewVoluumSection: FC<PreviewVoluumSectionProps> = ({
  result,
  applied,
}) => {
  const { flowQuery, draftState, provider } = useStandardDraftContext();

  if (flowQuery.isLoading) {
    return (
      <div className="flex justify-center py-6">
        <LoadingSpinner />
      </div>
    );
  }
  if (flowQuery.isError || !draftState.draft) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
        <AlertCircle className="h-4 w-4 shrink-0" aria-hidden />
        <span>
          Failed to load flow for preview.{' '}
          {flowQuery.error instanceof Error ? flowQuery.error.message : ''}
        </span>
      </div>
    );
  }

  return (
    <PreviewIndexProvider
      items={result.items}
      countryKey={result.countryKey}
      campaignId={result.campaignId}
      provider={result.provider}
      tracker={provider}
    >
      <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-hidden">
        <PreviewSummary result={result} applied={applied} />
        <StandardPathsWorkspace
          draft={draftState.draft}
          onPatch={draftState.patch}
        />
      </div>
    </PreviewIndexProvider>
  );
};

export default PreviewVoluumSection;
